import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  authRolesSelector,
  tenantRolesSelector,
} from '../store/auth.selectors';
import { map, filter, take, timeout, catchError } from 'rxjs/operators';
import { CoreRoutes } from '../../core/routes/core.routes';
import { InitialFlowErrorChange } from '../../core/store/core.actions';
import { InitialFlowErrorType } from '../../core/models/initial-flow-error-type.model';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard {
  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // Si estamos en iframe, esperar a que se carguen los roles
    if (window.self !== window.top) {
      return combineLatest([
        this.store.select(authRolesSelector),
        this.store.select(tenantRolesSelector).pipe(
          filter(roles => roles && roles.length > 0),
          take(1),
          timeout(10000),
          catchError(() => of([]))
        ),
      ]).pipe(
        map(([userRoles, customTenantRoles]) => {
          const requiredRoles: string[] = route.data?.roles ?? [];
          if (requiredRoles.length === 0) {
            customTenantRoles.forEach(tenantRole => {
              requiredRoles.push(tenantRole.id);
              requiredRoles.push(tenantRole.label);
            });
          }

          if (!userRoles || userRoles?.length === 0) {
            this.redirectToCoreErrorView();
            return false;
          }

          let isAllowed = false;
          requiredRoles.forEach(requiredRole => {
            if (userRoles.some(role => role === requiredRole)) {
              isAllowed = true;
            }
          });

          if (!isAllowed) {
            this.redirectToCoreErrorView();
          }

          return isAllowed;
        })
      );
    }

    // Si no estamos en iframe, usar lógica normal
    return combineLatest([
      this.store.select(authRolesSelector),
      this.store.select(tenantRolesSelector),
    ]).pipe(
      map(([userRoles, customTenantRoles]) => {
        const requiredRoles: string[] = route.data?.roles ?? [];
        if (requiredRoles.length === 0) {
          customTenantRoles.forEach(tenantRole => {
            requiredRoles.push(tenantRole.id);
            requiredRoles.push(tenantRole.label);
          });
        }
        if (!userRoles || userRoles?.length === 0) {
          this.redirectToCoreErrorView();
          return false;
        }
        let isAllowed = false;
        requiredRoles.forEach(requiredRole => {
          if (userRoles.some(role => role === requiredRole)) {
            isAllowed = true;
          }
        });
        if (!isAllowed) {
          this.redirectToCoreErrorView();
        }
        return isAllowed;
      })
    );
  }

  private redirectToCoreErrorView(): void {
    this.store.dispatch(
      new InitialFlowErrorChange({ error: InitialFlowErrorType.noRole })
    );
    const url = `/${CoreRoutes.root}/${CoreRoutes.forbidden}`;
    this.router.navigateByUrl(url);
  }
}
